import React from 'react';
import { FormGroup } from '../../../form/form-groups';

/**
 * Recovery strategy for form errors
 */
export enum RecoveryStrategy {
  /** Reset the form to its initial state */
  RESET_FORM = 'reset_form',
  
  /** Retry the last operation that caused the error */
  RETRY_OPERATION = 'retry_operation',
  
  /** Ignore the error and continue */
  IGNORE = 'ignore',
  
  /** Custom recovery strategy defined by the user */
  CUSTOM = 'custom'
}

/**
 * Error information provided to error fallback components
 * 
 * @template T - The type of form data
 */
export interface ErrorInfo<T> {
  /** The error that was caught */
  error: Error;
  
  /** Information about the error context */
  errorInfo?: React.ErrorInfo;
  
  /** The form groups at the time of the error */
  formGroups?: FormGroup<T>[];
  
  /** The operation that was being performed when the error occurred */
  operation?: string;
  
  /** Additional context data */
  context?: Record<string, any>;
}

/**
 * Props for the error fallback component
 * 
 * @template T - The type of form data
 */
export interface ErrorFallbackProps<T> {
  /** Error information */
  errorInfo: ErrorInfo<T>;
  
  /** Function to reset the error state */
  resetErrorBoundary: () => void;
  
  /** Function to apply a recovery strategy */
  recover: (strategy: RecoveryStrategy | ((errorInfo: ErrorInfo<T>) => void)) => void;
}

/**
 * Props for the FormErrorBoundary component
 * 
 * @template T - The type of form data
 */
export interface ErrorBoundaryProps<T> {
  /** Children to render */
  children: React.ReactNode;
  
  /** 
   * Component to render when an error occurs
   * If not provided, a default error UI will be shown
   */
  FallbackComponent?: React.ComponentType<ErrorFallbackProps<T>>;
  
  /** 
   * Render prop alternative to FallbackComponent
   */
  fallbackRender?: (props: ErrorFallbackProps<T>) => React.ReactNode;
  
  /**
   * Simple fallback UI as a React Element
   */
  fallback?: React.ReactNode;
  
  /**
   * Called when an error is caught
   */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  
  /**
   * Called when the error boundary recovers from an error
   */
  onReset?: () => void;
  
  /**
   * Default recovery strategy to apply when resetErrorBoundary is called
   * If not provided, defaults to RESET_FORM
   */
  defaultRecoveryStrategy?: RecoveryStrategy;
  
  /**
   * Custom recovery handler for the CUSTOM recovery strategy
   */
  onCustomRecovery?: (errorInfo: ErrorInfo<T>) => void;
  
  /**
   * Form groups to include in the error info
   */
  formGroups?: FormGroup<T>[];
}

/**
 * State for the FormErrorBoundary component
 * 
 * @template T - The type of form data
 */
export interface ErrorBoundaryState<T> {
  /** Whether an error has been caught */
  hasError: boolean;
  
  /** Error information */
  errorInfo: ErrorInfo<T> | null;
}